'use client';

import type { ReactNode } from 'react';
import { useAuth } from '@/platform/auth/useAuth';
import { setPostWatch, type ForumPost } from './api';
import { EmojiReactions } from './EmojiReactions';
import { ForumCommandMenu } from './ForumCommandMenu';
import './forum-actions.css';

type Props = {
  post: ForumPost;
  onUpdate?: () => void;
  showCommandMenu?: boolean;
  /** Vertical-specific actions (e.g. haul community validity votes on claim-linked posts). */
  validityVoteActions?: ReactNode;
  /** Product tour: emoji local-only; watch stays disabled without auth. */
  tourMode?: boolean;
};

export function PostActions({
  post,
  onUpdate,
  showCommandMenu = true,
  validityVoteActions,
  tourMode = false,
}: Props) {
  const { isAuthenticated } = useAuth();

  async function toggleWatch() {
    if (!isAuthenticated) return;
    await setPostWatch(post.id, !post.watching);
    onUpdate?.();
  }

  return (
    <div className="post-actions">
      {validityVoteActions}
      <button
        type="button"
        disabled={!isAuthenticated}
        className={`post-actions__icon-btn${post.watching ? ' post-actions__icon-btn--active' : ''}`}
        title={post.watching ? 'Watching' : 'Watch'}
        aria-label={post.watching ? 'Watching' : 'Watch'}
        onClick={() => void toggleWatch()}
      >
        <i className="uil uil-eye" aria-hidden />
      </button>
      <EmojiReactions postId={post.id} onUpdate={onUpdate} localOnly={tourMode} />
      {showCommandMenu && !tourMode && (
        <ForumCommandMenu kind="post" post={post} onUpdate={onUpdate} allowTitleEdit={!post.artifactType} />
      )}
    </div>
  );
}
